import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Loader from "../components/Loader";
import appConfig from "../../config/appConfig";
import * as pbi from "powerbi-client";
import { verifyPermission } from "../../helpers/permission";
import { ERROR_401 } from "../../config/errorPage";

const ClaimPowerBiReporting = (props) => {
  const { t, ready } = useTranslation();
  const embedContainerRef = useRef(null);
  const reportRef = useRef(null);

  if (!verifyPermission(props.userPermissions, "show-power-bi-report")) {
    window.location.href = ERROR_401;
  }

  document.title = "Satis client - " + (ready ? t("Rapport POWER-BI") : "");

  const [load, setLoad] = useState(true);
  const [data, setData] = useState(null);
  const [embedError, setEmbedError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportPdfMessage, setExportPdfMessage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoad(true);
    setEmbedError(null);
    try {
      const response = await axios.get(`${appConfig.apiDomaine}/power-bi-report`);
      setData(response.data);
    } catch (error) {
      console.error(error);
      setEmbedError(t("Impossible de charger le rapport Power BI."));
    } finally {
      setLoad(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data?.embedUrl || !data?.accessToken || !embedContainerRef.current || load) return;

    const container = embedContainerRef.current;
    const powerbiService = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    );

    const config = {
      type: "report",
      embedUrl: data.embedUrl,
      accessToken: data.accessToken,
      id: data.reportId,
      tokenType: pbi.models.TokenType.Embed,
      settings: {
        panes: {
          filters: { visible: true },
        //   pageNavigation: { visible: true },
        },
        zoomLevel: 0.552,
      },
    };

    try {
      const report = powerbiService.embed(container, config);
      reportRef.current = report;

      report.on("loaded", () => setEmbedError(null));
      report.on("error", (event) => {
        const err = event.detail;
        setEmbedError(err?.message || t("Erreur lors du chargement du rapport."));
      });
    } catch (err) {
      console.error(err);
      setEmbedError(err?.message || t("Erreur lors de l'intégration du rapport."));
    }

    return () => {
      try {
        powerbiService.reset(container);
      } catch (e) {
        console.warn(e);
      }
      reportRef.current = null;
    };
  }, [data, load, t]);

  const handleExportPdf = useCallback(async () => {
    const report = reportRef.current;
    if (!report) return;

    setExportingPdf(true);
    setExportPdfMessage(t("Préparation de l'export PDF (toutes les pages)..."));

    try {
      const pages = await report.getPages();

      if (!pages || pages.length === 0) {
        setExportPdfMessage(t("Aucune page à exporter."));
        setExportingPdf(false);
        setTimeout(() => setExportPdfMessage(null), 3000);
        return;
      }

      const total = pages.length;

      for (let i = 0; i < total; i++) {
        await report.setPage(pages[i].name);
        setExportPdfMessage(
          t(
            "Boîte d'impression pour la page {{current}}/{{total}}. Choisissez « Enregistrer en PDF » puis validez.",
            {
              current: i + 1,
              total,
            }
          )
        );
        await report.print();

        // Petite pause entre deux boîtes d'impression pour éviter d'enchaîner trop vite
        if (i < total - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      setExportPdfMessage(
        t("Export terminé. Vous pouvez fusionner les PDF si besoin dans un seul fichier.")
      );
      setTimeout(() => setExportPdfMessage(null), 5000);
    } catch (e) {
      console.error(e);
      setExportPdfMessage(t("Erreur lors de l'export PDF."));
      setTimeout(() => setExportPdfMessage(null), 4000);
    } finally {
      setExportingPdf(false);
    }
  }, [t]);

  return ready ? (
    <div
      className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader   kt-grid__item" id="kt_subheader">
        <div className="kt-container  kt-container--fluid ">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Rapport POWER-BI")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#icone" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a
                href="#button"
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link"
              >
                {t("Rapport POWER-BI")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        {!load && !embedError && data?.embedUrl && data?.accessToken && (
          <div className="d-flex align-items-center flex-wrap mb-3">
            <button
              type="button"
              className="btn btn-primary ml-auto"
              onClick={handleExportPdf}
              disabled={exportingPdf}
            >
              {exportingPdf ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                  {t("Export en cours...")}
                </>
              ) : (
                <>
                  <i className="flaticon2-download mr-2" />
                  {t("Exporter en PDF")}
                </>
              )}
            </button>
            {exportPdfMessage && (
              <span className="text-muted small mr-auto mt-2 mt-sm-0">
                {exportPdfMessage}
              </span>
            )}
          </div>
        )}
        {load ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Loader />
          </div>
        ) : embedError ? (
          <div className="alert alert-danger" role="alert">
            {embedError}
          </div>
        ) : data && (!data.embedUrl || !data.accessToken) ? (
          <div className="alert alert-warning" role="alert">
            {t("Données de rapport Power BI incomplètes.")}
          </div>
        ) : (
          <div
            ref={embedContainerRef}
            className="powerbi-embed-container"
            style={{ height: "calc(100vh - 180px)", minHeight: "500px", width: "95%" }}
          />
        )}
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
    plan: state.plan.plan,
    lead: state?.user?.user?.staff?.is_pilot_lead || false,
    staff: state?.user?.user?.staff?.id || null,
  };
};

export default connect(mapStateToProps)(ClaimPowerBiReporting);
